// src/pages/home/home.js

import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { companyInfo } from "../../CompanyInfo/CompanyInfo";
import "./home.css";
import {
  Search, PhoneCall, CheckCircle2, Handshake, Trophy,
  ArrowRight, ChevronDown, Briefcase, Send, Code2, Layers,
  Sparkles, ShieldCheck, Users
} from "lucide-react";

// ─── DATA DEFINITIONS ────────────────────────────────────────────────────────

const servicesSlides = [
  {
    id: "recruitment",
    category: "HR & Recruitment",
    badgeColor: "purple",
    title: "Recruitment",
    desc: "EduITalent delivers end-to-end recruitment services across India and Globally, connecting you with candidates who drive growth.",
    highlights: [
      "Rigorous competency and skill vetting",
      "Shortlists delivered in 3 to 5 business days",
      "End-to-end recruitment logistics managed"
    ],
    image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=600",
    link: "/services/recruitment-services"
  },
  {
    id: "executive",
    category: "HR & Recruitment",
    badgeColor: "purple",
    title: "C-Level & Leadership Hiring",
    desc: "Targeted executive search campaigns to place C-suite leaders and top-level managers who drive organizational growth.",
    highlights: [
      "Rigorous leadership fit vetting framework",
      "Global network of C-level tech executives",
      "98.5% retention rate on placements"
    ],
    image: "https://img.etimg.com/thumb/width-1600,height-900,imgsize-1197306,resizemode-75,msid-103480185/jobs/c-suite/the-art-of-change-leadership-mid-career-courses-for-mid-level-professionals-eyeing-cxo-positions.jpg",
    link: "/services/recruitment-services"
  },
  {
    id: "executive-search",
    category: "HR & Recruitment",
    badgeColor: "purple",
    title: "Executive Search",
    desc: "Targeted executive search campaigns to place senior leaders, functional managers, and directors who drive organizational growth.",
    highlights: [
      "Rigorous fit and competency assessment",
      "Global network of executive professionals",
      "Confidential headhunting and vetting"
    ],
    image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=600",
    link: "/services/recruitment-services"
  },
  {
    id: "payroll",
    category: "HR & Recruitment",
    badgeColor: "purple",
    title: "Third Party Payroll",
    desc: "Simple, compliant payroll support for salary processing, attendance coordination, ESI/PF, tax records, and contractor documentation.",
    highlights: [
      "ESI, PF, and local tax compliance managed",
      "Saves up to 70% in administrative overhead",
      "Seamless Noida & Dubai legal compliance"
    ],
    image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=600",
    link: "/services/payroll-services"
  },
  {
    id: "outsourcing",
    category: "HR & Recruitment",
    badgeColor: "purple",
    title: "HR Outsourcing & Consulting",
    desc: "Complete human resources outsourcing, from onboarding workflows and training programs to performance monitoring and workspace compliance.",
    highlights: [
      "Streamlined onboarding & employee lifecycle",
      "Custom organizational benchmarks",
      "Scale operations without administrative burden"
    ],
    image: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=600",
    link: "/contact"
  },
  {
    id: "staffing",
    category: "HR & Recruitment",
    badgeColor: "purple",
    title: "Entry & Mid-Level Hiring",
    desc: "Sourcing high-caliber talent across all experience levels — from fresh graduates to senior professionals — for IT, non-IT, sales, operations, and support roles.",
    highlights: [
      "Screened candidates for diverse business roles",
      "Fast shortlists for urgent hiring needs",
      "Flexible contract-to-hire or permanent hiring"
    ],
    image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=600",
    link: "/services/entry-middle-level-hiring"
  },
  {
    id: "web-dev",
    category: "Technology Solutions",
    badgeColor: "blue",
    title: "High-End Web Development",
    desc: "Bespoke web applications and enterprise platforms engineered using modern React, Next.js, and cloud native architectures.",
    highlights: [
      "Custom interactive dashboard designs",
      "Optimized for SEO, speed, and responsiveness",
      "Robust backend systems built with Node & AWS"
    ],
    image: "https://images.unsplash.com/photo-1547082299-de196ea013d6?w=600",
    link: "/services/web-development"
  },
  {
    id: "app-dev",
    category: "Technology Solutions",
    badgeColor: "blue",
    title: "Native Mobile App Development",
    desc: "Premium cross-platform mobile apps for iOS and Android built on Flutter or React Native, optimized for fast performance.",
    highlights: [
      "Beautiful, fluid responsive UI/UX designs",
      "Secure payment and third-party integrations",
      "App Store & Google Play launch support"
    ],
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600",
    link: "/services/app-development"
  },
  {
    id: "tech-hiring",
    category: "Technology Solutions",
    badgeColor: "blue",
    title: "Technical Sourcing & Vetting",
    desc: "Helping engineering teams scale by sourcing, testing, and presenting top software engineers who pass rigorous coding challenges.",
    highlights: [
      "Rigorous automated coding challenges",
      "Senior engineer live architecture reviews",
      "Only top ~3% of software candidates placed"
    ],
    image: "https://images.unsplash.com/photo-1607799279861-4dd421887fb3?w=600",
    link: "/services/entry-middle-level-hiring"
  }
];

const heroServiceSlides = [
  {
    title: "Recruitment",
    label: "Talent acquisition partner",
    desc: "EduITalent delivers end-to-end recruitment services across India and Globally, matching businesses with candidates who drive growth.",
    image: "https://media.licdn.com/dms/image/v2/C5612AQERrLNGH0trYw/article-cover_image-shrink_720_1280/article-cover_image-shrink_720_1280/0/1610722479498?e=2147483647&v=beta&t=chyF54JyoNDE97Oaqnzx85lHLHmqJ6D7uxhHxzLS0Mw"
  },
  {
    title: "Third Party Payroll",
    label: "Payroll made simple",
    desc: "We manage salary cycles, employee records, statutory compliance, and payroll coordination so your team can focus on work.",
    image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600"
  },
  {
    title: "HR Outsourcing",
    label: "People operations support",
    desc: "Get help with onboarding, HR documentation, attendance support, employee coordination, and routine HR operations.",
    image: "https://thumbs.dreamstime.com/b/outsourcing-human-resources-business-internet-technology-concept-126587976.jpg",
  },
  {
    title: "Web, App & Digital Services",
    label: "Technical delivery support",
    desc: "We also build websites, mobile apps, and social media growth systems for businesses that need reliable digital execution.",
    image: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=600"
  },
  {
    title: "Entry & Mid-Level Hiring",
    label: "Talent acquisition solutions",
    desc: "Sourcing high-caliber talent across all experience levels, from fresh graduates to senior professionals for diverse business roles.",
    image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=600"
  },
  {
    title: "C-Level & Leadership Hiring",
    label: "Executive Search & Placements",
    desc: "Targeted campaigns to recruit C-suite executives, directors, and top-tier leaders who drive business transformation.",
    image: "https://img.etimg.com/thumb/width-1600,height-900,imgsize-1197306,resizemode-75,msid-103480185/jobs/c-suite/the-art-of-change-leadership-mid-career-courses-for-mid-level-professionals-eyeing-cxo-positions.jpg"
  },
  {
    title: "Technical Sourcing & Vetting",
    label: "Scale your engineering team",
    desc: "Find, vet, and hire top software engineers, system designers, and technology specialists using rigorous skill assessments.",
    image: "https://images.unsplash.com/photo-1607799279861-4dd421887fb3?w=600"
  }
];

const marqueeIndustries = [
  "FinTech", "Healthcare", "E-Commerce", "SaaS Platforms", "Logistics & Aviation",
  "EdTech & Education", "Artificial Intelligence", "Cloud Infrastructure", "Cybersecurity",
  "Telecom & Media", "Energy & Utilities", "Manufacturing & Infra"
];

const stats = [
  { value: "5,000+", label: "Professionals Sourced", icon: <Users size={24} />, color: "#7C3AED" },
  { value: "98.5%", label: "Placement Retention", icon: <ShieldCheck size={24} />, color: "#10B981" },
  { value: "15+", label: "Years HR & IT Experience", icon: <Briefcase size={24} />, color: "#2563EB" },
  { value: "₹10Cr+", label: "Compliant Payroll Managed", icon: <Trophy size={24} />, color: "#F59E0B" },
];

const visualServices = [
  {
    title: "Payroll & HR Operations",
    desc: "Organized payroll cycles, documentation, compliance, and employee coordination.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600",
    link: "/services/payroll-services"
  },
  {
    title: "Web & App Development",
    desc: "Focused product teams building practical websites, applications, and digital platforms.",
    image: "https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=600",
    link: "/services/web-development"
  },
  {
    title: "Recruitment & Outsourcing",
    desc: "Structured searches, onboarding support, and dependable outsourced HR processes.",
    image: "https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=600",
    link: "/services/recruitment-services"
  }
];

const processSteps = [
  {
    num: "01",
    icon: <Search size={24} />,
    title: "Intake & Discovery",
    desc: "In-depth consultations to align on your tech stack goals or custom organizational talent benchmarks.",
    color: "#2563EB",
    bg: "#EFF6FF",
    bgPhoto: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400",
  },
  {
    num: "02",
    icon: <Layers size={24} />,
    title: "Strategy & Mapping",
    desc: "Designing custom technology scoping roadmaps or building target passive candidate talent maps.",
    color: "#7C3AED",
    bg: "#F5F3FF",
    bgPhoto: "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400",
  },
  {
    num: "03",
    icon: <Code2 size={24} />,
    title: "Agile Execution",
    desc: "Developing features in regular weekly sprints or launching target candidate vetting workflows.",
    color: "#EC4899",
    bg: "#FDF2F8",
    bgPhoto: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=400",
  },
  {
    num: "04",
    icon: <RocketIcon size={24} />, // custom inline visual or standard icon mapping
    title: "Delivery & Support",
    desc: "Deploying high-quality platforms to cloud servers or onboarding pre-vetted specialists.",
    color: "#10B981",
    bg: "#ECFDF5",
    bgPhoto: "https://images.unsplash.com/photo-1512428559087-560fa5ceab42?w=400",
  },
];

// Helper custom icon
function RocketIcon(props) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" /><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </svg>
  );
}

function WhatsAppIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M20.52 3.48A11.9 11.9 0 0 0 12.02 0C5.37 0 .15 5.22.15 11.87c0 2.12.55 4.18 1.6 5.99L0 24l6.32-1.66a11.83 11.83 0 0 0 5.7 1.46h.01c6.64 0 11.86-5.22 11.86-11.87 0-3.22-1.26-6.24-3.37-8.45ZM12.03 21.58h-.01a9.69 9.69 0 0 1-4.94-1.35l-.35-.21-3.76.99 1-3.66-.23-.37a9.61 9.61 0 0 1-1.47-5.1c0-5.33 4.33-9.66 9.66-9.66 2.58 0 5 1 6.83 2.84a9.59 9.59 0 0 1 2.84 6.83c0 5.33-4.33 9.65-9.57 9.65Zm5.61-7.66c-.31-.15-1.84-.91-2.12-1.01-.28-.1-.49-.15-.7.15-.21.31-.8 1.01-.98 1.22-.18.21-.36.24-.67.08-.31-.15-1.32-.49-2.5-1.56-.92-.82-1.54-1.84-1.72-2.15-.18-.31-.02-.48.13-.63.14-.14.31-.36.46-.54.15-.18.2-.31.31-.52.1-.21.05-.39-.03-.54-.08-.15-.7-1.68-.96-2.3-.25-.6-.5-.52-.7-.53h-.59c-.21 0-.54.08-.83.39-.28.31-1.09 1.07-1.09 2.61 0 1.53 1.11 3.01 1.26 3.22.15.21 2.2 3.37 5.34 4.73.75.32 1.34.51 1.79.66.75.24 1.43.2 1.97.12.6-.09 1.84-.75 2.1-1.48.26-.72.26-1.34.18-1.48-.07-.15-.28-.23-.59-.38Z" />
    </svg>
  );
}

const vettingSteps = [
  {
    num: "01",
    title: "Requirement & Role Mapping",
    desc: "We first understand the role, department, salary range, shift needs, reporting structure, and location expectations.",
    rate: "Clear Scope",
    color: "#2563EB",
    bg: "#EFF6FF",
  },
  {
    num: "02",
    title: "Document & Background Check",
    desc: "Profiles are checked for identity records, experience proof, previous employment details, and basic professional reliability.",
    rate: "Verified Records",
    color: "#7C3AED",
    bg: "#F5F3FF",
  },
  {
    num: "03",
    title: "Payroll & Compliance Review",
    desc: "For payroll and outsourced teams, we review attendance flow, ESI/PF needs, local labour rules, and monthly payout readiness.",
    rate: "Compliance Ready",
    color: "#EC4899",
    bg: "#FDF2F8",
  },
  {
    num: "04",
    title: "Communication & Fit Check",
    desc: "Shortlisted people are assessed for communication, punctuality, customer handling, reporting discipline, and team fit.",
    rate: "Workplace Fit",
    color: "#10B981",
    bg: "#ECFDF5",
  },
  {
    num: "05",
    title: "Onboarding & Monthly Support",
    desc: "We support joining, documentation, payroll handover, attendance coordination, and recurring HR follow-ups after placement.",
    rate: "Ongoing Support",
    color: "#F59E0B",
    bg: "#FFFBEB",
  },
];

const faqs = [
  {
    q: "What services does EduITalent provide?",
    a: "Two practices: IT Solutions (web apps, mobile apps, social media campaigns) and Talent Recruitment (C-suite search, entry/mid-level staffing, third-party payroll). Both run out of Noida and Dubai.",
  },
  {
    q: "How does the recruitment process work?",
    a: "Discovery first, then we run searches through our global talent database. Every candidate goes through five vetting stages including live coding tests. Structured interviews happen before any offer is made.",
  },
  {
    q: "Do you serve clients outside India?",
    a: "Yes — our Dubai branch handles Middle East and GCC work. We regularly place talent internationally and deliver offshore software for clients who need it.",
  },
  {
    q: "What technologies do you use for IT delivery?",
    a: "React, Next.js, Flutter, Kotlin, Swift, Node.js, Python, PostgreSQL, and major cloud infrastructure — AWS, Docker, Vercel. We choose the stack that fits the problem.",
  },
  {
    q: "Can you handle payroll and compliant staffing?",
    a: "Yes — salary cycles, ESI/PF, local labour compliance, tax docs, and contractor logistics in both India and the UAE. We own the complexity so you don't have to.",
  },
  {
    q: "How do you ensure candidate quality?",
    a: "A strict 5-stage vetting framework: Resume Audit, Automated Coding Tests, Live Design Evaluations, Behavioural Screening, and Onboarding Verification. Only the top ~3% are placed.",
  },
];

// ─── COMPONENT DEFINITION ────────────────────────────────────────────────────

export default function Home() {
  const [openFaq, setOpenFaq] = useState(null);
  const [dashMode, setDashMode] = useState("talent");
  const [activeSlide, setActiveSlide] = useState(0);
  const [activeHeroSlide, setActiveHeroSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);

  const progressTimerRef = useRef(null);
  const revealRefs = useRef([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("hm-shown"); }),
      { threshold: 0.08 }
    );
    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const heroTimer = setInterval(() => {
      setActiveHeroSlide((prev) => (prev + 1) % heroServiceSlides.length);
    }, 4200);

    return () => clearInterval(heroTimer);
  }, []);

  useEffect(() => {
    if (!isPlaying) {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      return;
    }

    progressTimerRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setActiveSlide((prevSlide) => (prevSlide + 1) % servicesSlides.length);
          return 0;
        }
        return prev + 1; // 50ms * 100 = 5 seconds autoplay loop
      });
    }, 50);

    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, [isPlaying]);

  const handleSlideChange = (index) => {
    setActiveSlide(index);
    setProgress(0);
  };

  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % servicesSlides.length);
    setProgress(0);
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + servicesSlides.length) % servicesSlides.length);
    setProgress(0);
  };

  const addReveal = (el) => { if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el); };
  const currentHeroSlide = heroServiceSlides[activeHeroSlide];
    const whatsappUrl = `https://wa.me/917838305432?text=${encodeURIComponent("Hi EduITalent, I would like to connect.")}`;


  return (
    <div className="home-page">

      {/* ======================================================
          SECTION 1 — HERO
      ====================================================== */}
      <section className="hm-hero">
        <div className="hm-hero-bg-pattern" />
        <div className="hm-hero-bg-gradient" style={{ backgroundImage: "url(/images/hero_bg_graphic.png)", backgroundSize: "cover", backgroundPosition: "center", opacity: 0.07, inset: 0, width: "100%", height: "100%" }} />

        <div className="hm-container">
          <div className="hm-hero-layout">

            {/* Left Hero Area */}
            <div className="hm-hero-content">
              <div className="hm-hero-pill">
                <span className="hm-hero-pill-dot" />
                Empowering Scale &nbsp;·&nbsp; HR &amp; IT Recruitment Specialists
              </div>

              <h1>
                Strategic Recruitment &amp;<br />
                <span className="hm-highlight-blue">Compliant HR Solutions.</span>
              </h1>

              <p className="hm-hero-desc">
                We help companies manage payroll and HR outsourcing while also delivering technical services like web development, mobile app development, and social media marketing.
              </p>

              <div className="hm-hero-btns">
                <Link to="/contact" className="hm-btn-primary">
                  Hire Expert Talent <ArrowRight size={16} />
                </Link>
                <Link to="/contact" className="hm-btn-secondary">
                  Explore Tech Services
                </Link>
              </div>

              <div className="hm-hero-check-list">
                {[
                  "Executive search & C-suite placements",
                  "Compliant third-party payroll & ESI/PF processing",
                  "Web development, app development, and social media marketing"
                ].map((item, idx) => (
                  <div key={idx} className="hm-hero-check-item">
                    <CheckCircle2 size={15} className="hm-check-icon-blue" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Hero Area (Dual-Path Interactive Dashboard Mock) */}
            <div className="hm-hero-right">
              <div className="hm-hero-service-slider animate-float">
                <div className="hm-hero-photo-wrap">
                  <img src={currentHeroSlide.image} alt={currentHeroSlide.title} className="hm-hero-service-photo" />
                  <div className="hm-hero-photo-shade" />
                  <div className="hm-hero-slide-copy">
                    <span>{currentHeroSlide.label}</span>
                    <h3>{currentHeroSlide.title}</h3>
                    <p>{currentHeroSlide.desc}</p>
                  </div>
                </div>

                <div className="hm-hero-slide-controls" aria-label="Hero service slideshow controls">
                  {heroServiceSlides.map((slide, idx) => (
                    <button
                      key={slide.title}
                      className={`hm-hero-slide-dot ${idx === activeHeroSlide ? "active" : ""}`}
                      onClick={() => setActiveHeroSlide(idx)}
                      aria-label={`Show ${slide.title}`}
                    />
                  ))}
                </div>

                <div className="hm-hero-mini-services">
                  {["Recruitment", "Payroll", "HR Outsourcing", "Mid-Level Hiring", "C-Level Hiring", "Tech Sourcing", "Digital Services"].map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              </div>
              <div className="hm-interactive-dashboard animate-float">
                <div className="hm-dash-nav">
                  <button
                    className={`hm-dash-nav-btn ${dashMode === "tech" ? "active-blue" : ""}`}
                    onClick={() => setDashMode("tech")}
                  >
                    <Code2 size={14} /> Technology Desk
                  </button>
                  <button
                    className={`hm-dash-nav-btn ${dashMode === "talent" ? "active-purple" : ""}`}
                    onClick={() => setDashMode("talent")}
                  >
                    <Users size={14} /> Talent Matching
                  </button>
                </div>

                <div className="hm-dash-body">
                  {dashMode === "tech" ? (
                    <div className="hm-tech-view animate-fade">
                      <div className="hm-tv-bar">
                        <span className="dot r" /><span className="dot y" /><span className="dot g" />
                        <span className="hm-tv-url">https://eduitalent.com/pipeline</span>
                      </div>
                      <div className="hm-tv-code">
                        <div className="code-ln"><span>1</span> <span className="k">import</span> React <span className="k">from</span> <span className="s">"react"</span>;</div>
                        <div className="code-ln"><span>2</span> <span className="k">const</span> Platform = () =&gt; &#123;</div>
                        <div className="code-ln"><span>3</span> &nbsp;&nbsp;<span className="k">return</span> &lt;<span className="tag">Scale</span> load=<span className="s">"100%"</span> /&gt;;</div>
                        <div className="code-ln"><span>4</span> &#125;;</div>
                      </div>
                      <div className="hm-tv-logs">
                        <div className="log-line green">● Build Successful - production bundle</div>
                        <div className="log-line blue">● AWS Cloudfront cache invalidated</div>
                      </div>
                    </div>
                  ) : (
                    <div className="hm-talent-view animate-fade">
                      <div className="hm-tal-card">
                        <div className="hm-tal-header">
                          <div className="hm-tal-avatar">SK</div>
                          <div>
                            <h4>Sanjay K.</h4>
                            <span>Senior React Native Engineer</span>
                          </div>
                          <span className="hm-tal-badge">99% Match</span>
                        </div>
                        <div className="hm-tal-tags">
                          <span>React Native</span><span>Redux</span><span>Swift</span>
                        </div>
                        <div className="hm-tal-status">
                          <div className="hm-tal-status-dot green" /> Active Pipeline: Interviewing
                        </div>
                      </div>
                      <div className="hm-tal-stats">
                        <div className="stat-card">
                          <span>Vetted Pool</span>
                          <strong>4.8K+</strong>
                        </div>
                        <div className="stat-card">
                          <span>Verified Skills</span>
                          <strong>100%</strong>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 2 — INFINITE MARQUEE
      ====================================================== */}
      <section className="hm-marquee-section">
        <div className="hm-marquee-title">Sectors &amp; Skill Domains We Support</div>
        <div className="hm-marquee-container">
          <div className="hm-marquee-track">
            {marqueeIndustries.concat(marqueeIndustries).map((ind, idx) => (
              <span key={idx} className="hm-marquee-item">
                <Sparkles size={14} className="hm-marquee-spark" /> {ind}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 3 — STATS BAR
      ====================================================== */}
      <section className="hm-stats-bar">
        <div className="hm-container">
          <div className="hm-stats-grid">
            {stats.map((s, i) => (
              <div key={i} className="hm-stats-item">
                <div className="hm-stats-icon" style={{ color: s.color, background: `${s.color}14` }}>
                  {s.icon}
                </div>
                <div>
                  <div className="hm-stats-value" style={{ color: s.color }}>{s.value}</div>
                  <div className="hm-stats-label">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 4 — CORE SERVICES SHOWCASE (SLIDESHOW)
      ====================================================== */}
      <section className="hm-visual-services hm-reveal" ref={addReveal}>
        <div className="hm-container">
          <div className="hm-visual-services-head">
            <div>
              <span className="hm-badge-lbl">What We Handle</span>
              <h2>People, payroll, and digital delivery.</h2>
            </div>
            <p>Three connected service areas that help growing businesses operate with less friction.</p>
          </div>

          <div className="hm-visual-services-grid">
            {visualServices.map((service) => (
              <Link to={service.link} className="hm-visual-service-card" key={service.title}>
                <img src={service.image} alt={service.title} />
                <div className="hm-visual-service-shade" />
                <div className="hm-visual-service-copy">
                  <h3>{service.title}</h3>
                  <p>{service.desc}</p>
                  <span>Explore service <ArrowRight size={15} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="hm-showcase-section hm-reveal" ref={addReveal}>
        <div className="hm-container">
          <div className="hm-section-hdr-center">
            <span className="hm-badge-lbl">Core Specializations</span>
            <h2>Our Core Services &amp; Solutions</h2>
            <p>Explore HR, payroll, outsourcing, entry &amp; mid-level hiring, and selected IT support services.</p>
          </div>

          <div
            className="hm-showcase-container"
            onMouseEnter={() => setIsPlaying(false)}
            onMouseLeave={() => setIsPlaying(true)}
          >
            {/* Left Column: Quick-Select Tabs */}
            <div className="showcase-tabs-container">
              <div className="showcase-tabs-header">
                <span className="tabs-hdr-title">Browse Services</span>
                <span className="tabs-hdr-hint">{isPlaying ? "Auto-playing" : "Paused"}</span>
              </div>
              <div className="showcase-tabs-list">
                {servicesSlides.map((slide, idx) => (
                  <button
                    key={slide.id}
                    className={`showcase-tab-btn ${idx === activeSlide ? "active" : ""} ${slide.badgeColor}`}
                    onClick={() => handleSlideChange(idx)}
                  >
                    <div className="tab-btn-content">
                      <span className="tab-category">{slide.category}</span>
                      <span className="tab-title">{slide.title}</span>
                    </div>
                    {idx === activeSlide && (
                      <div className="tab-progress-bg">
                        <div className="tab-progress-bar" style={{ width: `${progress}%` }} />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Right Column: Slide Display Card */}
            <div className="showcase-display-card">
              <div className="slide-display-wrapper">
                {/* Slide Image */}
                <div className="slide-image-container">
                  <img
                    src={servicesSlides[activeSlide].image}
                    alt={servicesSlides[activeSlide].title}
                    className="slide-image-view"
                  />
                  <div className={`slide-category-tag ${servicesSlides[activeSlide].badgeColor}`}>
                    {servicesSlides[activeSlide].category}
                  </div>
                </div>

                {/* Slide Info */}
                <div className="slide-info-container">
                  <span className="slide-number">Service 0{activeSlide + 1} / 0{servicesSlides.length}</span>
                  <h3>{servicesSlides[activeSlide].title}</h3>
                  <p className="slide-desc">{servicesSlides[activeSlide].desc}</p>

                  <div className="slide-highlights-list">
                    {servicesSlides[activeSlide].highlights.map((h, i) => (
                      <div key={i} className="slide-highlight-item">
                        <CheckCircle2 size={16} className={`highlight-check-icon ${servicesSlides[activeSlide].badgeColor}`} />
                        <span>{h}</span>
                      </div>
                    ))}
                  </div>

                  <div className="slide-footer-actions">
                    <Link to={servicesSlides[activeSlide].link} className={`slide-action-btn ${servicesSlides[activeSlide].badgeColor}`}>
                      Learn More <ArrowRight size={16} />
                    </Link>

                    {/* Manual controls */}
                    <div className="slide-manual-arrows">
                      <button onClick={prevSlide} className="manual-arrow-btn" aria-label="Previous service">
                        ←
                      </button>
                      <button onClick={nextSlide} className="manual-arrow-btn" aria-label="Next service">
                        →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 5 — rigourous VETTING FRAMEWORK
      ====================================================== */}
      <section className="hm-vetting-section">
        <div className="hm-container">
          <div className="hm-section-hdr-center hm-reveal" ref={addReveal}>
            <span className="hm-badge-lbl">Quality First</span>
            <h2>Our 5-Step HR Quality Framework</h2>
            <p>How we keep payroll, HR outsourcing, staffing, documentation, and onboarding organized before people join your team.</p>
          </div>

          <div className="hm-vetting-layout">
            <div className="hm-vetting-list">
              {vettingSteps.map((step, idx) => (
                <div className="hm-vetting-row hm-reveal" key={idx} ref={addReveal} style={{ transitionDelay: `${idx * 0.08}s` }}>
                  <div className="hm-vetting-num" style={{ color: step.color }}>{step.num}</div>
                  <div className="hm-vetting-icon-box" style={{ background: step.bg, color: step.color }}>
                    {idx === 0 && <Search size={22} />}
                    {idx === 1 && <ShieldCheck size={22} />}
                    {idx === 2 && <Briefcase size={22} />}
                    {idx === 3 && <ShieldCheck size={22} />}
                    {idx === 4 && <Handshake size={22} />}
                  </div>
                  <div className="hm-vetting-body">
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                  <div className="hm-vetting-badge-box">
                    <span className="hm-vetting-pass-badge" style={{ background: step.bg, color: step.color }}>
                      {step.rate}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="hm-vetting-visual hm-reveal" ref={addReveal}>
              <div className="hm-vetting-img-card">
                <img src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=600" alt="HR outsourcing quality review" className="hm-vetting-img" />
                <div className="hm-vetting-proof-grid">
                  <span>Payroll Ready</span>
                  <span>Verified Records</span>
                  <span>Onboarding Support</span>
                </div>
                <div className="hm-vetting-card-overlay">
                  <h4>
                    <Sparkles size={16} className="hm-spark-icon" /> HR Quality Desk
                  </h4>
                  <p>Reliable HR checks, payroll readiness, and onboarding support for business teams.</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ======================================================
          SECTION 6 — AGILE PROCESS TIMELINE
      ====================================================== */}
      <section className="hm-process-section">
        <div className="hm-container">
          <div className="hm-section-hdr-center hm-reveal" ref={addReveal}>
            <span className="hm-badge-lbl">Delivery pipeline</span>
            <h2>How We Build &amp; Work</h2>
            <p>From requirements gathering to post-deployment checks, we execute with structured clarity.</p>
          </div>

          <div className="hm-process-grid">
            {processSteps.map((step, i) => (
              <div className="hm-process-card hm-reveal" key={i} ref={addReveal} style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="hm-process-card-bg" style={{ backgroundImage: `url(${step.bgPhoto})` }} />
                <span className="hm-proc-step-num">{step.num}</span>
                <div className="hm-proc-icon-box" style={{ background: step.bg, color: step.color }}>
                  {step.icon}
                </div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
                {i < processSteps.length - 1 && <div className="hm-proc-connector">→</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 7 — FAQ
      ====================================================== */}
      <section className="hm-faq-section hm-section hm-surface">
        <div className="hm-container">

          {/* ── Section header ── */}
          <div className="hm-section-hdr hm-reveal" ref={addReveal}>
            <div className="hm-section-hdr-left">
              <span className="hm-badge-lbl">Common questions</span>
              {/* ① serif italic heading */}
              <h2>Things people<br /><em>ask us.</em></h2>
            </div>
            <span className="hm-section-num"></span>
          </div>

          {/* ① Two-column FAQ layout */}
          <div className="hm-faq-layout">

            {/* ── Left aside ── */}
            <div className="hm-faq-aside hm-reveal" ref={addReveal}>
              <p>
                If something isn't answered here, a 15-minute call usually clears it up.
              </p>

              {/* ③ Response-time mini card */}
              <div className="hm-faq-aside-card">
                <div className="hm-faq-aside-label">Response time</div>
                <div className="hm-faq-aside-val">&lt; 2 hrs</div>
                <div className="hm-faq-aside-sub">on business days</div>
              </div>
            </div>

            {/* ── Right accordion ── */}
            <div className="hm-faq-list">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className={`hm-faq-item${openFaq === i ? " hm-faq-open" : ""}`}
                >
                  <button
                    className="hm-faq-question"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    aria-expanded={openFaq === i}
                  >
                    {/* ② plain zero-padded index — no coloured badge */}
                    <span className="hm-faq-num">
                      {String(i + 1).padStart(2, "0")}
                    </span>

                    <span className="hm-faq-q-text">{faq.q}</span>

                    <span className="hm-faq-chevron">
                      <ChevronDown
                        size={16}
                        style={{
                          transform: openFaq === i ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.25s",
                        }}
                      />
                    </span>
                  </button>

                  {/* ⑥ Answer indented under question text */}
                  {openFaq === i && (
                    <div className="hm-faq-answer">
                      <p>{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 8 — CTA BANNER
      ====================================================== */}
      <section className="hm-cta-banner">
        <div className="hm-cta-banner-bg" style={{ backgroundImage: "radial-gradient(circle at 15% 50%, rgba(255, 255, 255, 0.06) 0%, transparent 45%), radial-gradient(circle at 85% 20%, rgba(255, 255, 255, 0.06) 0%, transparent 45%), url(/images/cta_bg_graphic.png)", backgroundSize: "cover", backgroundPosition: "center", opacity: 0.12 }} />
        <div className="hm-container">
          <div className="hm-cta-banner-inner">
            <div className="hm-cta-banner-content">
              <h2>Ready to Engineer Custom Platforms &amp; Hire Teams?</h2>
              <p>
                Get in touch today for a free technology strategy discovery or staffing consultation.
                Let's discuss how our Noida and Dubai networks can best serve your targets.
              </p>
              <div className="hm-cta-banner-btns">
                <Link to="/contact" className="hm-cta-white-btn">
                  Get Free Consultation <ArrowRight size={16} />
                </Link>
                <a href={`mailto:${companyInfo.email}`} className="hm-cta-ghost-btn">
                  Email Support
                </a>
              </div>
            </div>
            <div className="hm-cta-contact-cards">
              <a href={`tel:${companyInfo.phone}`} className="hm-cta-contact-card">
                <div className="hm-cta-contact-icon"><PhoneCall size={16} /></div>
                <div>
                  <div className="hm-cta-contact-label">India HQ</div>
                  <div className="hm-cta-contact-val">{companyInfo.phone}</div>
                </div>
              </a>
              <a href={`tel:${companyInfo.phone2}`} className="hm-cta-contact-card">
                <div className="hm-cta-contact-icon"><PhoneCall size={16} /></div>
                <div>
                  <div className="hm-cta-contact-label">Dubai Office</div>
                  <div className="hm-cta-contact-val">{companyInfo.phone2}</div>
                </div>
              </a>
              <a href={`mailto:${companyInfo.email}`} className="hm-cta-contact-card">
                <div className="hm-cta-contact-icon"><Send size={16} /></div>
                <div>
                  <div className="hm-cta-contact-label">Email Address</div>
                  <div className="hm-cta-contact-val">{companyInfo.email}</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      <a
        href={whatsappUrl}
        className="hm-whatsapp-float"
        target="_blank"
        rel="noreferrer"
        aria-label="Chat with EduITalent on WhatsApp at 7838305432"
      >
        <span className="hm-whatsapp-float-icon">
          <WhatsAppIcon width={20} height={20} />
        </span>
      </a>

    </div>
  );
}
